import { useRef, useState } from "react";
import CalendarIcon from "./CalendarIcon";
import PayPalCheckout from "./PayPalCheckout";
import {
  assets,
  bookingPayment,
  defaultTimezone,
  timezones
} from "../data/siteData";
import { createBooking } from "../services/bookings";
import { formatTimeInSast } from "../utils/time";

export default function BookingSection() {
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedBookingId, setSavedBookingId] = useState("");
  const [selectedTimezone, setSelectedTimezone] = useState(defaultTimezone);
  const [suggestedCallTime, setSuggestedCallTime] = useState("");
  const suggestedCallTimeRef = useRef(null);

  const selectedTimezoneData = timezones.find(
    (timezone) => timezone.value === selectedTimezone
  );
  const suggestedCallTimeSast = formatTimeInSast(
    suggestedCallTime,
    selectedTimezoneData?.offset
  );

  async function handleBookingSubmit(event) {
    event.preventDefault();
    setIsSubmitting(true);
    setFormError("");
    setSavedBookingId("");

    const form = event.currentTarget;
    const formData = new FormData(form);
    const bookingDetails = Object.fromEntries(formData.entries());

    try {
      const booking = await createBooking(bookingDetails);

      saveBookingDraft(bookingDetails, { bookingId: booking.id });

      setSavedBookingId(booking.id);
      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
      setFormError(getBookingErrorMessage(error));

      saveBookingDraft(bookingDetails, { saveError: error.message });

      console.error("Booking submission failed", error);
    }
  }

  function openSuggestedCallPicker() {
    const input = suggestedCallTimeRef.current;

    if (!input) {
      return;
    }

    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }

    input.focus();
  }

  if (savedBookingId) {
    return (
      <section id="book" className="booking-section" aria-labelledby="payment-title">
        <div className="booking-panel booking-payment-panel">
          <PayPalCheckout bookingId={savedBookingId} />
        </div>
      </section>
    );
  }

  return (
    <section id="book" className="booking-section" aria-labelledby="book-title">
      <div className="booking-panel">
        <div className="booking-copy">
          <p className="eyebrow">Book your strategy session</p>
          <h2 id="book-title">Ready To Level Up</h2>
          <p>
            {bookingPayment.displayAmount} includes full performance analysis,
            documented learning roadmap, and a 1 hour private strategy session.
          </p>

          <div className="payment-card" aria-label="Payment options">
            <div>
              <span className="payment-icon" aria-hidden="true">
                <img src={assets.paypalLogo} alt="" />
              </span>
              <span className="payment-label">Available now</span>
              <strong>PayPal {bookingPayment.displayAmount}</strong>
            </div>
            <div>
              <span className="payment-icon payment-icon-muted" aria-hidden="true">
                <img src={assets.cardIcon} alt="" />
              </span>
              <span className="payment-label">Coming soon</span>
              <strong>Card</strong>
            </div>
          </div>

          <p className="booking-note">
            Your booking details are saved first, then you will continue to a
            payment page for the {bookingPayment.displayAmount} payment.
          </p>
        </div>

        <form className="booking-form" onSubmit={handleBookingSubmit}>
          {formError ? (
            <div className="form-alert form-alert-error" role="alert">
              {formError}
            </div>
          ) : null}

          <div className="field-grid">
            <label>
              <span>Full name</span>
              <input name="fullName" type="text" autoComplete="name" required />
            </label>

            <label>
              <span>Email address</span>
              <input name="email" type="email" autoComplete="email" required />
            </label>
          </div>

          <div className="field-grid">
            <label>
              <span>Suggested call time</span>
              <span className={`input-with-icon${suggestedCallTime ? " has-value" : ""}`}>
                <span className="input-icon-button" aria-hidden="true">
                  <CalendarIcon />
                </span>
                <span className="datetime-placeholder">Select date and time</span>
                <input
                  ref={suggestedCallTimeRef}
                  name="suggestedCallTime"
                  type="datetime-local"
                  aria-label="Suggested call time"
                  onClick={openSuggestedCallPicker}
                  onChange={(event) => setSuggestedCallTime(event.target.value)}
                  required
                />
              </span>
            </label>

            <label>
              <span>Timezone</span>
              <select
                name="timezone"
                value={selectedTimezone}
                onChange={(event) => setSelectedTimezone(event.target.value)}
                required
              >
                <option value="" disabled>
                  Select your timezone
                </option>
                {timezones.map((timezone) => (
                  <option key={timezone.value} value={timezone.value}>
                    {timezone.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <input name="suggestedCallTimeSast" type="hidden" value={suggestedCallTimeSast} />

          <label>
            <span>Who are you as a musician?</span>
            <textarea
              name="musicianBackground"
              rows="4"
              minLength="10"
              placeholder="Tell me where you are musically, church role, experience level, and what feels stuck."
              required
            />
          </label>

          <label>
            <span>Your goals</span>
            <textarea
              name="goals"
              rows="4"
              minLength="10"
              placeholder="What do you want to unlock in your playing?"
              required
            />
          </label>

          <label>
            <span>Pianist you look up to</span>
            <input
              name="inspirationPianist"
              type="text"
              minLength="2"
              placeholder="e.g. Cory Henry, Kevin Bond, Jason Tyson"
              required
            />
          </label>

          <div className="field-grid three-up">
            <label>
              <span>Video link 1</span>
              <input name="videoLinkOne" type="url" placeholder="https://" required />
            </label>

            <label>
              <span>Video link 2</span>
              <input name="videoLinkTwo" type="url" placeholder="https://" required />
            </label>

            <label>
              <span>Video link 3</span>
              <input name="videoLinkThree" type="url" placeholder="https://" required />
            </label>
          </div>

          <label>
            <span>Social links optional</span>
            <textarea
              name="socialLinks"
              rows="3"
              placeholder="Instagram, TikTok, YouTube, church page, or anywhere you share music."
            />
          </label>

          <div className="booking-actions">
            <button
              className="button button-primary"
              type="submit"
              disabled={isSubmitting || Boolean(savedBookingId)}
            >
              {isSubmitting ? (
                <>
                  <span className="button-loader" aria-hidden="true" />
                  Saving Booking...
                </>
              ) : (
                "Continue with PayPal"
              )}
            </button>
            <span>Card payments coming soon.</span>
          </div>

        </form>
      </div>
    </section>
  );
}

function getBookingErrorMessage(error) {
  if (error?.code === "permission-denied") {
    return "We couldn't save your booking. Please check that all required fields are filled correctly, including all 3 video links.";
  }

  return (
    error?.message ||
    "We couldn't save your booking yet. Please check your connection and try again."
  );
}

function saveBookingDraft(bookingDetails, extraDetails) {
  sessionStorage.setItem(
    "gospelKeysBookingDraft",
    JSON.stringify({
      ...bookingDetails,
      ...extraDetails,
      submittedAt: new Date().toISOString()
    })
  );
}
